import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css"; // ✅ make sure bootstrap-icons is installed

const LicenseForm = ({
  license,
  userId,
  isAdmin,
  isSigned,
  onSaved,
  onDeleted,
  onCancelNew,
}) => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [form, setForm] = useState({
    dl_id: license.dl_id || null,
    status: license.dl_status || "primary",
    licenseNumber: license.dl_license_no || "",
    licenseClass: license.dl_class || [],
    licenseCondition: license.dl_condition || [],
    country: license.dl_country || "",
    province: license.dl_province || "",
    expiryDate: license.dl_expdate ? license.dl_expdate.substring(0, 10) : "",
    file: null,
    uploadedFileName: license.dl_license || null,
    originalFileName: license.dl_license_original || null,
  });

  const [isEditing, setIsEditing] = useState(!!license.isNew);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [classes, setClasses] = useState([]);
  const [conditions, setConditions] = useState([]);

  // Fetch dropdowns
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE}/api/dropdowns/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCountries(res.data))
      .catch(() => setCountries([]));
  }, [API_BASE]);

  useEffect(() => {
    if (form.country !== "Canada") {
      setProvinces([]);
      return;
    }
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE}/api/dropdowns/provinces/1`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setProvinces(res.data))
      .catch(() => setProvinces([]));
  }, [form.country, API_BASE]);

  useEffect(() => {
    if (form.country !== "Canada" || !form.province) {
      setClasses([]);
      setConditions([]);
      return;
    }
    const token = localStorage.getItem("token");
    const selectedProvince = provinces.find(
      (p) => p.province_name === form.province
    );
    if (!selectedProvince) return;

    axios
      .get(`${API_BASE}/api/dropdowns/license-classes/${selectedProvince.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setClasses(res.data))
      .catch(() => setClasses([]));

    axios
      .get(
        `${API_BASE}/api/dropdowns/license-conditions/${selectedProvince.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => setConditions(res.data))
      .catch(() => setConditions([]));
  }, [form.country, form.province, provinces, API_BASE]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setForm({ ...form, file });
    } else {
      alert("Only PDF files allowed!");
    }
  };

  const handleSubmit = async () => {
    // 🛑 First time creating a license → file is required
    if (license.isNew && !form.file) {
      alert("Please upload a PDF file for the license.");
      return;
    }

    // 🛑 Editing an existing license but NO file + NO old file → still required
    if (!license.isNew && !form.file && !form.uploadedFileName) {
      alert("A PDF file is required for this license.");
      return;
    }

    const formData = new FormData();

    // Only send new file if user actually uploaded one
    if (form.file) {
      formData.append("file", form.file);
    }

    formData.append("userId", userId);
    formData.append("dl_id", form.dl_id || "");
    formData.append("status", form.status);
    formData.append("license_number", form.licenseNumber);
    formData.append("class", form.licenseClass);
    formData.append("condition", form.licenseCondition);
    formData.append("province", form.province);
    formData.append("country", form.country);
    formData.append("expiry_date", form.expiryDate);

    try {
      await axios.post(
        `${API_BASE}/api/driverlicense/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert("License saved!");
      setIsEditing(false);
      onSaved();
    } catch (err) {
      console.error(err);
      alert("Save failed");
    }
  };

  const handleDelete = async () => {
    if (
      form.status === "other" &&
      form.dl_id &&
      window.confirm("Delete this license?")
    ) {
      try {
        await axios.delete(`${API_BASE}/api/driverlicense/${form.dl_id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        alert("License deleted!");
        onDeleted();
      } catch (err) {
        console.error(err);
        alert("Delete failed");
      }
    }
  };

  return (
    <div className="p-3 border rounded mb-4 position-relative">
      <h5>{form.status === "primary" ? "Primary License" : "Other License"}</h5>

      {/* Top-right action icons */}
      <div className="position-absolute top-0 end-0 m-2">
        {isAdmin ? (
          // Admin always allowed
          <>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-success me-2"
                  onClick={handleSubmit}
                  title="Save"
                >
                  <i className="bi bi-check-circle"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    if (license.isNew) {
                      // remove from list
                      onCancelNew();
                    } else {
                      // just exit edit mode
                      setIsEditing(false);
                    }
                  }}
                  title="Cancel"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-warning me-2"
                  onClick={() => setIsEditing(true)}
                  title="Edit"
                >
                  <i className="bi bi-pencil-square"></i>
                </button>
                {form.status === "other" && form.dl_id && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleDelete}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </>
            )}
          </>
        ) : !isSigned ? (
          // Normal user only if NOT signed
          <>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-success me-2"
                  onClick={handleSubmit}
                  title="Save"
                >
                  <i className="bi bi-check-circle"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setIsEditing(false)}
                  title="Cancel"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-warning me-2"
                  onClick={() => setIsEditing(true)}
                  title="Edit"
                >
                  <i className="bi bi-pencil-square"></i>
                </button>
                {form.status === "other" && form.dl_id && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleDelete}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </>
            )}
          </>
        ) : null}
      </div>

      {/* Full form */}
      <form>
        {/* Country + Province */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Country *</label>
            <select
              className="form-select"
              value={form.country}
              disabled={!isEditing}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              required
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.country_name}>
                  {c.country_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Province *</label>
            {form.country === "Canada" ? (
              <select
                className="form-select"
                value={form.province}
                disabled={!isEditing}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                required
              >
                <option value="">Select Province</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.province_name}>
                    {p.province_name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="form-control"
                value={form.province}
                disabled={!isEditing}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
              />
            )}
          </div>
        </div>

        {/* License Number + Class + Condition + Expiry */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">License Number *</label>
            <input
              type="text"
              className="form-control"
              value={form.licenseNumber}
              disabled={!isEditing}
              onChange={(e) =>
                setForm({ ...form, licenseNumber: e.target.value })
              }
              required
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">License Class *</label>
            {form.country === "Canada" ? (
              <Select
                isMulti
                value={form.licenseClass.map((c) => ({ value: c, label: c }))}
                options={classes.map((c) => ({
                  value: c.class_code,
                  label: c.description
                    ? `${c.class_code} - ${c.description}`
                    : c.class_code,
                }))}
                isDisabled={!isEditing}
                onChange={(selected) =>
                  setForm({
                    ...form,
                    licenseClass: selected.map((s) => s.value),
                  })
                }
                required
              />
            ) : (
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 1,3,5"
                value={
                  Array.isArray(form.licenseClass)
                    ? form.licenseClass.join(",")
                    : form.licenseClass
                }
                disabled={!isEditing}
                onChange={(e) =>
                  setForm({
                    ...form,
                    licenseClass: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                required
              />
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">Condition</label>
            {form.country === "Canada" ? (
              <Select
                isMulti
                value={form.licenseCondition.map((c) => ({
                  value: c,
                  label: c,
                }))}
                options={conditions.map((c) => ({
                  value: c.condition_code,
                  label: c.description
                    ? `${c.condition_code} - ${c.description}`
                    : c.condition_code,
                }))}
                isDisabled={!isEditing}
                onChange={(selected) =>
                  setForm({
                    ...form,
                    licenseCondition: selected.map((s) => s.value),
                  })
                }
              />
            ) : (
              <input
                type="text"
                className="form-control"
                placeholder="e.g. A,B,C"
                value={
                  Array.isArray(form.licenseCondition)
                    ? form.licenseCondition.join(",")
                    : form.licenseCondition
                }
                disabled={!isEditing}
                onChange={(e) =>
                  setForm({
                    ...form,
                    licenseCondition: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">Expiry Date *</label>
            <input
              type="date"
              className="form-control"
              value={form.expiryDate}
              disabled={!isEditing}
              onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
              required
            />
          </div>
        </div>

        {/* File Upload */}
        <div className="mb-3">
          <label className="form-label">Select PDF file *</label>
          <input
            type="file"
            className="form-control"
            accept="application/pdf"
            disabled={!isEditing}
            onChange={handleFileChange}
            required={license.isNew || !form.uploadedFileName}
          />
        </div>

        {form.uploadedFileName && form.originalFileName && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Uploaded Version</div>
            <a
              href={`${API_BASE}/uploads/driverlicenses/${form.uploadedFileName}`}
              target="_blank"
              rel="noopener noreferrer"
              download={form.originalFileName}
            >
              {form.originalFileName}
            </a>
          </div>
        )}
      </form>
    </div>
  );
};

export default LicenseForm;
